import React, { useState, useEffect } from 'react';
import { useSocket } from '../context/SocketContext';
import { useAuth } from '../context/AuthContext';
import { authAPI, chatAPI, controlAPI } from '../api/api';
import toast from 'react-hot-toast';
import InstagramChat from '../components/InstagramChat';

const chatStoreKey = (userId) => `rp_parent_chat_${userId}`;

export default function ChatPage() {
  const { user } = useAuth();
  const { socket } = useSocket();
  const [children, setChildren] = useState([]);
  const [selectedChildId, setSelectedChildId] = useState('');
  const [conversations, setConversations] = useState({});

  useEffect(() => {
    const key = chatStoreKey(user?._id || user?.id || 'anon');
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        setConversations(JSON.parse(raw));
      }
    } catch (_) {
      // Ignore malformed local chat cache
    }
  }, [user]);

  useEffect(() => {
    const key = chatStoreKey(user?._id || user?.id || 'anon');
    localStorage.setItem(key, JSON.stringify(conversations));
  }, [conversations, user]);

  useEffect(() => {
    const loadChildren = async () => {
      try {
        const res = await authAPI.children();
        const linked = res.data?.children || [];
        setChildren(linked);
        setSelectedChildId((prev) => prev || linked[0]?._id || '');
      } catch (err) {
        toast.error('Could not load children list');
      }
    };

    loadChildren();
  }, []);

  useEffect(() => {
    if (!selectedChildId) return;

    const loadHistory = async () => {
      try {
        const res = await chatAPI.getParentHistory(selectedChildId, 120);
        const history = res.data?.messages || [];
        setConversations((prev) => ({
          ...prev,
          [selectedChildId]: history.length > 0 ? history : (prev[selectedChildId] || []),
        }));
      } catch (err) {
        toast.error('Could not load chat history');
      }
    };

    loadHistory();
  }, [selectedChildId]);

  useEffect(() => {
    if (!socket) return;

    const handleChildMessage = (data) => {
      const childId = data?.childId;
      if (!childId || !data?.message) return;

      setConversations((prev) => {
        const current = prev[childId] || [];
        return {
          ...prev,
          [childId]: [
            ...current,
            {
              fromChild: true,
              message: data.message,
              timestamp: data.timestamp || Date.now(),
            },
          ],
        };
      });

      setChildren((prev) => prev.map((c) => (
        c._id === childId ? { ...c, name: data?.childName || c.name } : c
      )));
    };

    socket.on('child_chat_message', handleChildMessage);

    const handleCallRequest = (data) => {
      const from = data?.childName || data?.parentName || 'Other user';
      const callType = data?.callType === 'video' ? 'video' : 'audio';
      toast(`${from} sent a ${callType} call request`);
    };

    socket.on('chat_call_request', handleCallRequest);

    return () => {
      socket.off('child_chat_message', handleChildMessage);
      socket.off('chat_call_request', handleCallRequest);
    };
  }, [socket]);

  const selectedChild = children.find((c) => c._id === selectedChildId) || null;
  const chatMessages = selectedChildId ? (conversations[selectedChildId] || []) : [];

  const handleSendMessage = (message) => {
    if (!socket || !selectedChildId) {
      toast.error('Please select a child first');
      return;
    }

    const newMessage = {
      fromChild: false,
      message: message,
      timestamp: Date.now(),
      parentId: user?.id,
      parentName: user?.name
    };

    setConversations((prev) => {
      const current = prev[selectedChildId] || [];
      return {
        ...prev,
        [selectedChildId]: [...current, newMessage],
      };
    });

    socket.emit('parent_chat_message', {
      childId: selectedChildId,
      message: message,
      timestamp: Date.now(),
      parentName: user?.name
    });
  };

  const handleAudioCall = async () => {
    if (!selectedChildId) {
      toast.error('Please select a child first');
      return;
    }

    try {
      await controlAPI.requestMic(selectedChildId);
      toast.success('Audio call request sent to child device');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send audio request');
    }
  };

  const handleVideoCall = async () => {
    if (!selectedChildId) {
      toast.error('Please select a child first');
      return;
    }

    try {
      await controlAPI.requestCamera(selectedChildId, 'front');
      toast.success('Video call request sent to child device');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send video request');
    }
  };

  return (
    <div className="chat-page-container">
      <div className="chat-page-header">
        <h2>💬 Chat with Child</h2>
        <p className="chat-page-subtitle">Real-time encrypted messaging</p>
      </div>

      <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--border)' }}>
        <div className="child-selector" style={{ marginBottom: 0 }}>
          {children.map((child) => (
            <button
              key={child._id}
              className={`child-chip ${selectedChildId === child._id ? 'active' : ''}`}
              onClick={() => setSelectedChildId(child._id)}
            >
              <span className="child-avatar">{child?.name?.[0]?.toUpperCase() || 'C'}</span>
              <span>{child.name}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="chat-page-content">
        {!selectedChildId ? (
          <div className="chat-no-device">
            <div className="no-device-icon">📱</div>
            <div className="no-device-title">No Connected Child Device</div>
            <div className="no-device-text">
              A child device must be connected and approved before you can start chatting.
            </div>
            <div className="no-device-actions">
              <div className="device-status">
                <span className="status-indicator inactive"></span>
                Waiting for connection...
              </div>
            </div>
          </div>
        ) : (
          <InstagramChat
            messages={chatMessages}
            onSendMessage={handleSendMessage}
            userName={user?.name}
            otherUserName={selectedChild?.name || 'Child Device'}
            isParent={true}
            onAudioCall={handleAudioCall}
            onVideoCall={handleVideoCall}
          />
        )}
      </div>

      <style>{`
        .chat-page-container {
          display: flex;
          flex-direction: column;
          height: 100%;
          min-height: 100vh;
          background: var(--bg-primary);
        }

        .chat-page-header {
          padding: 24px;
          border-bottom: 1px solid var(--border);
          background: linear-gradient(135deg, rgba(17,24,39,0.8), rgba(13,21,40,0.8));
          backdrop-filter: blur(10px);
        }

        .chat-page-header h2 {
          font-size: 24px;
          font-weight: 700;
          color: var(--text-primary);
          margin-bottom: 8px;
        }

        .chat-page-subtitle {
          font-size: 13px;
          color: var(--text-secondary);
        }

        .chat-page-content {
          flex: 1;
          display: flex;
          overflow: hidden;
          margin-bottom: 60px;
        }

        @media (max-width: 768px) {
          .chat-page-content {
            margin-bottom: 80px;
          }
        }

        .chat-no-device {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          text-align: center;
          color: var(--text-secondary);
          padding: 40px;
        }

        .no-device-icon {
          font-size: 64px;
          margin-bottom: 20px;
          opacity: 0.6;
          animation: float 3s ease-in-out infinite;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-20px); }
        }

        .no-device-title {
          font-size: 20px;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 8px;
        }

        .no-device-text {
          font-size: 14px;
          color: var(--text-secondary);
          max-width: 400px;
          margin-bottom: 30px;
          line-height: 1.6;
        }

        .no-device-actions {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .device-status {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid var(--border);
          padding: 12px 24px;
          border-radius: 12px;
          font-size: 13px;
          color: var(--text-secondary);
        }

        .status-indicator {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          animation: pulse-blink 1.5s infinite;
        }

        .status-indicator.inactive {
          background: var(--red);
        }

        @keyframes pulse-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}
